// @tier: PREMIUM — données du dashboard analytique
import { useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { dashboardApiService, dashboardUtils } from '../services/dashboardApi';

/**
 * tr_dashboard — logique métier de la page Dashboard Premium.
 * Chargement des stats via React Query, auto-refresh 5 min, utilitaires de formatage.
 *
 * Retourne : { stats, busStatus, recentActivities, studentStats, parentStats,
 *              dashboardData, loading, error, refreshing, lastUpdated,
 *              refresh, loadDashboardData, getStatusColor, getActivityColor,
 *              formattedStats, systemHealth, busOccupancyRate, activeBusCount,
 *              totalBusCount, hasData, isDataFresh }
 */
export const useDashboardReal = () => {
  const {
    data: dashboardData,
    isPending: loading,
    isFetching,
    error: queryError,
    dataUpdatedAt,
    refetch,
  } = useQuery({
    queryKey: ['dashboardOverview'],
    queryFn: () => dashboardApiService.getAllDashboardData(),
    refetchInterval: 5 * 60 * 1000, // auto-refresh toutes les 5 minutes
  });

  const error = queryError?.message ?? null;
  const refreshing = isFetching && !loading;

  const stats = dashboardData?.overview ?? null;
  const busStatus = dashboardData?.buses?.buses || [];
  const recentActivities = dashboardUtils.formatActivitiesForDisplay(dashboardData?.activities);
  const studentStats = dashboardData?.students ?? null;
  const parentStats = dashboardData?.parents ?? null;
  const notificationStats = dashboardData?.notifications ?? null;
  const lastUpdated = dataUpdatedAt ? new Date(dataUpdatedAt).toISOString() : null;

  const refresh = useCallback(async () => {
    await refetch();
  }, [refetch]);

  const getStatusColor = useCallback((status) => dashboardUtils.getBusStatusColor(status), []);
  const getActivityColor = useCallback((type) => dashboardUtils.getActivityColor(type), []);

  const formattedStats = dashboardUtils.formatStatsForCards(stats);
  const systemHealth = dashboardUtils.calculateSystemHealth(stats);

  const totalCapacity = busStatus.reduce((s, b) => s + (b.capacity || 0), 0);
  const totalStudents = busStatus.reduce((s, b) => s + (b.students || 0), 0);
  const busOccupancyRate = totalCapacity > 0 ? Math.round((totalStudents / totalCapacity) * 100) : 0;
  const activeBusCount = busStatus.filter((b) => b.status === 'En route' || b.status === 'active').length;

  return {
    stats,
    busStatus,
    recentActivities,
    studentStats,
    parentStats,
    notificationStats,
    dashboardData,
    loading,
    error,
    refreshing,
    lastUpdated,
    refresh,
    loadDashboardData: refresh,
    getStatusColor,
    getActivityColor,
    formattedStats,
    systemHealth,
    busOccupancyRate,
    activeBusCount,
    totalBusCount: busStatus.length,
    hasData: !!stats,
    isDataFresh: lastUpdated && Date.now() - new Date(lastUpdated).getTime() < 10 * 60 * 1000,
  };
};

/**
 * KPIs flotte avancés (trajets/jour 30j, durée moyenne, arrêts desservis/skippés) — frontDashboard.jsx.
 */
export const useFleetStats = () =>
  useQuery({
    queryKey: ['dashboardFleetStats'],
    queryFn: () => dashboardApiService.getFleetStats(),
  });

/**
 * Liste des parents avec statut d'activation + dernière connexion (30j) — frontDashboard.jsx.
 */
export const useParentsDetail = () =>
  useQuery({
    queryKey: ['dashboardParentsDetail'],
    queryFn: () => dashboardApiService.getParentsDetail(),
  });

/**
 * Timeline notifications envoyées/délivrées/ouvertes par jour (30j) — frontDashboard.jsx.
 */
export const useNotificationsTimeline = () =>
  useQuery({
    queryKey: ['dashboardNotificationsTimeline'],
    queryFn: () => dashboardApiService.getNotificationsTimeline(),
  });

export default useDashboardReal;
